/* eslint-disable prettier/prettier */
import AddIcon from '@mui/icons-material/Add'
import CallSplitIcon from '@mui/icons-material/CallSplit'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
} from '@mui/material'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'

import ErrorBanner from '../../components/shared/ErrorBanner'
import { SegmentService } from '../../services/sendingDomainService'
import { parseCSV } from '../../utils/csv'
import {
    ACCENT,
    BRAND,
    FONT,
    ICON,
    ON_ORANGE,
    RADIUS,
    STATUS,
    SURFACE,
    TEXT,
    menuProps,
    pageSx,
} from '../../theme/tokens'

/**
 * Cross-channel segmentation.
 *
 * "Ability to segment out a set of customers and a different
 * email/sms/whatsapp should exist." The pipeline wizard sets channels for a
 * whole pipeline and Bulk Send takes one CSV with one template set, so there
 * was no way to say "these 500 get WhatsApp, those 2,000 get email".
 *
 * Preview only -- this never enqueues. The operator reviews cohort counts and
 * the per-cohort pre-send reports, then dispatches each payload through the
 * existing bulk send. A segmentation mistake is therefore a wasted preview
 * rather than a wrong send.
 *
 * MUTUAL EXCLUSIVITY is enforced server-side: cohorts are evaluated in
 * declared order and the first match claims each recipient. Overlaps are still
 * reported, because a recipient matching three cohorts means the rules need
 * tightening even though the send was correct.
 */

const CHANNELS = [
    'email',
    'whatsapp',
    'sms',
    'voice',
    'push_notification',
    'slack',
]
const OPS = [
    'eq',
    'ne',
    'in',
    'not_in',
    'contains',
    'gt',
    'gte',
    'lt',
    'lte',
    'exists',
]

const inputSx = { '& .MuiInputBase-input': { typography: 'caption' } }

const newSegment = (i) => ({
    name: `Cohort ${i + 1}`,
    channel: 'email',
    template_id: '',
    match: 'all',
    rules: [{ field: '', op: 'eq', value: '' }],
})

const RuleRow = ({ rule, columns, onChange, onRemove, canRemove }) => (
    <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'stretch' }}>
        <Select
            size="small"
            value={rule.field}
            displayEmpty
            onChange={(e) => onChange({ ...rule, field: e.target.value })}
            MenuProps={menuProps}
            sx={{ flex: 2, minWidth: 110, typography: 'caption' }}
        >
            <MenuItem value="" disabled>
                field
            </MenuItem>
            {columns.map((c) => (
                <MenuItem key={c} value={c}>
                    {c}
                </MenuItem>
            ))}
        </Select>
        <Select
            size="small"
            value={rule.op}
            onChange={(e) => onChange({ ...rule, op: e.target.value })}
            MenuProps={menuProps}
            sx={{ flex: 1, minWidth: 88, typography: 'caption' }}
        >
            {OPS.map((o) => (
                <MenuItem key={o} value={o}>
                    {o}
                </MenuItem>
            ))}
        </Select>
        <TextField
            size="small"
            placeholder="value"
            value={rule.value ?? ''}
            onChange={(e) => onChange({ ...rule, value: e.target.value })}
            sx={{ flex: 2, minWidth: 90, ...inputSx }}
        />
        <Button
            size="small"
            variant="outlined"
            disabled={!canRemove}
            onClick={onRemove}
            sx={{
                minWidth: 34,
                px: 0,
                height: 33,
                py: 0,
                boxSizing: 'border-box',
            }}
        >
            <DeleteOutlineIcon sx={{ fontSize: ICON.md }} />
        </Button>
    </Box>
)
RuleRow.propTypes = {
    rule: PropTypes.object,
    columns: PropTypes.array,
    onChange: PropTypes.func,
    onRemove: PropTypes.func,
    canRemove: PropTypes.bool,
}

const SegmentCard = ({
    segment,
    index,
    columns,
    onChange,
    onRemove,
    count,
}) => {
    const isCatchAll = (segment.rules || []).length === 0

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                mb: 2,
                background: SURFACE.card,
                border: `1px solid ${SURFACE.border}`,
                borderLeft: `3px solid ${BRAND.orange}`,
                borderRadius: `${RADIUS.md}px`,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    gap: 1,
                    mb: 2,
                    alignItems: 'stretch',
                    flexWrap: 'wrap',
                }}
            >
                <TextField
                    size="small"
                    value={segment.name}
                    onChange={(e) =>
                        onChange({ ...segment, name: e.target.value })
                    }
                    sx={{ flex: 2, minWidth: 120, ...inputSx }}
                    inputProps={{ 'aria-label': 'cohort name' }}
                />
                <Select
                    size="small"
                    value={segment.channel}
                    onChange={(e) =>
                        onChange({ ...segment, channel: e.target.value })
                    }
                    MenuProps={menuProps}
                    sx={{ flex: 1, minWidth: 130, typography: 'caption' }}
                >
                    {CHANNELS.map((c) => (
                        <MenuItem key={c} value={c}>
                            {c}
                        </MenuItem>
                    ))}
                </Select>
                <TextField
                    size="small"
                    placeholder="template_id"
                    value={segment.template_id}
                    onChange={(e) =>
                        onChange({ ...segment, template_id: e.target.value })
                    }
                    sx={{ flex: 1.5, minWidth: 110, ...inputSx }}
                />
                {count !== undefined && (
                    <Chip
                        label={`${count.toLocaleString()} recipients`}
                        size="small"
                        sx={{
                            height: 33,
                            borderRadius: `${RADIUS.sm}px`,
                            typography: 'caption',
                            fontWeight: 700,
                            color: count
                                ? STATUS.success.fg
                                : STATUS.neutral.fg,
                            background: count
                                ? STATUS.success.bg
                                : STATUS.neutral.bg,
                        }}
                    />
                )}
                <Button
                    size="small"
                    variant="outlined"
                    onClick={onRemove}
                    sx={{
                        minWidth: 34,
                        px: 0,
                        height: 33,
                        py: 0,
                        boxSizing: 'border-box',
                    }}
                >
                    <DeleteOutlineIcon sx={{ fontSize: ICON.md }} />
                </Button>
            </Box>

            {isCatchAll ? (
                <Typography
                    sx={{ typography: 'caption', color: STATUS.warning.fg }}
                >
                    No rules — this cohort claims every remaining recipient.
                    Only valid as the last cohort.
                </Typography>
            ) : (
                <>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 1,
                        }}
                    >
                        <Typography
                            sx={{ typography: 'caption', color: TEXT.muted }}
                        >
                            match
                        </Typography>
                        <Select
                            size="small"
                            value={segment.match}
                            onChange={(e) =>
                                onChange({ ...segment, match: e.target.value })
                            }
                            MenuProps={menuProps}
                            sx={{ typography: 'caption', minWidth: 70 }}
                        >
                            <MenuItem value="all">all</MenuItem>
                            <MenuItem value="any">any</MenuItem>
                        </Select>
                    </Box>
                    {(segment.rules || []).map((rule, ri) => (
                        <RuleRow
                            key={ri}
                            rule={rule}
                            columns={columns}
                            canRemove={segment.rules.length > 1}
                            onChange={(next) => {
                                const rules = [...segment.rules]
                                rules[ri] = next
                                onChange({ ...segment, rules })
                            }}
                            onRemove={() =>
                                onChange({
                                    ...segment,
                                    rules: segment.rules.filter(
                                        (_, i) => i !== ri
                                    ),
                                })
                            }
                        />
                    ))}
                </>
            )}

            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon sx={{ fontSize: ICON.sm }} />}
                    onClick={() =>
                        onChange({
                            ...segment,
                            rules: [
                                ...(segment.rules || []),
                                { field: '', op: 'eq', value: '' },
                            ],
                        })
                    }
                    sx={{ typography: 'caption', height: 28, py: 0 }}
                >
                    Rule
                </Button>
                {!isCatchAll && (
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => onChange({ ...segment, rules: [] })}
                        sx={{ typography: 'caption', height: 28, py: 0 }}
                    >
                        Make catch-all
                    </Button>
                )}
            </Box>
            <Typography
                sx={{ typography: 'caption', color: TEXT.faint, mt: 1 }}
            >
                cohort {index + 1} — evaluated in order; the first match claims
                each recipient
            </Typography>
        </Paper>
    )
}
SegmentCard.propTypes = {
    segment: PropTypes.object,
    index: PropTypes.number,
    columns: PropTypes.array,
    onChange: PropTypes.func,
    onRemove: PropTypes.func,
    count: PropTypes.number,
}

const Segments = () => {
    const [eventType, setEventType] = useState('')
    const [csvText, setCsvText] = useState('')
    const [segments, setSegments] = useState([newSegment(0)])
    const [result, setResult] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [columns, setColumns] = useState([])

    // Column names drive the rule field dropdown, so a typo cannot silently
    // create a rule that matches nothing.
    useEffect(() => {
        if (!csvText.trim()) {
            setColumns([])
            return
        }
        try {
            const parsed = parseCSV(csvText)
            setColumns(parsed?.headers || [])
        } catch {
            setColumns([])
        }
    }, [csvText])

    const identitiesFromCsv = () => {
        const parsed = parseCSV(csvText)
        const headers = parsed?.headers || []
        const rows = parsed?.rows || []
        const emailCol = headers.find((h) => /email/i.test(h))
        const identities = {}
        const others = {}
        if (emailCol) {
            identities.email = rows.map((r) => r[emailCol] ?? '')
        }
        headers
            .filter((h) => h !== emailCol)
            .forEach((h) => {
                others[h] = rows.map((r) => r[h] ?? '')
            })
        return { identities, others }
    }

    const canPreview =
        eventType.trim() &&
        csvText.trim() &&
        segments.length > 0 &&
        segments.every((s) => s.name.trim() && s.template_id.trim())

    const handlePreview = async () => {
        setLoading(true)
        setError('')
        setResult(null)
        try {
            const { identities, others } = identitiesFromCsv()
            const res = await SegmentService.preview({
                eventType: eventType.trim(),
                identities,
                others,
                segments: segments.map((s) => ({
                    name: s.name.trim(),
                    channel: s.channel,
                    template_id: s.template_id.trim(),
                    match: s.match,
                    rules: (s.rules || [])
                        .filter((r) => r.field)
                        .map((r) => ({
                            field: r.field,
                            op: r.op,
                            value: r.value,
                        })),
                })),
            })
            setResult(res?.data || null)
        } catch (err) {
            // Server-side validation is authoritative (duplicate names,
            // unreachable cohort after a catch-all, unsupported op); surface
            // its message rather than duplicating the rules here.
            setError(
                err?.response?.data?.error || 'Could not preview these segments'
            )
        } finally {
            setLoading(false)
        }
    }

    const summary = result?.summary
    const countFor = (name) =>
        summary?.segments?.find((s) => s.name === name)?.count

    return (
        <Box sx={{ ...pageSx, p: { xs: 2, md: 4 } }}>
            <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        mb: 0.5,
                    }}
                >
                    <CallSplitIcon
                        sx={{ fontSize: ICON.xl, color: BRAND.orange }}
                    />
                    <Typography
                        sx={{
                            typography: 'h2',
                            fontWeight: 700,
                            color: TEXT.primary,
                        }}
                    >
                        Segments
                    </Typography>
                </Box>
                <Typography
                    sx={{ typography: 'caption', color: TEXT.muted, mb: 3 }}
                >
                    Split one audience into cohorts, each with its own channel
                    and template. Preview only — nothing is sent from here.
                </Typography>

                {error && (
                    <Box sx={{ mb: 2 }}>
                        <ErrorBanner message={error} />
                    </Box>
                )}

                <Paper
                    elevation={0}
                    sx={{
                        p: 2.5,
                        mb: 3,
                        background: SURFACE.card,
                        border: `1px solid ${SURFACE.border}`,
                        borderRadius: `${RADIUS.md}px`,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1.5,
                            mb: 2,
                            flexWrap: 'wrap',
                        }}
                    >
                        <TextField
                            size="small"
                            placeholder="event_type"
                            value={eventType}
                            onChange={(e) => setEventType(e.target.value)}
                            sx={{ flex: 1, minWidth: 180, ...inputSx }}
                        />
                    </Box>
                    <TextField
                        multiline
                        rows={6}
                        fullWidth
                        placeholder={
                            'email,grade,days_since_open\na@x.com,8,5\nb@x.com,9,200'
                        }
                        value={csvText}
                        onChange={(e) => setCsvText(e.target.value)}
                        sx={{
                            '& .MuiInputBase-input': {
                                fontFamily: FONT.mono,
                                typography: 'caption',
                            },
                        }}
                    />
                    {columns.length > 0 && (
                        <Typography
                            sx={{
                                typography: 'caption',
                                color: TEXT.faint,
                                mt: 1,
                            }}
                        >
                            columns: {columns.join(', ')}
                        </Typography>
                    )}
                </Paper>

                {segments.map((s, i) => (
                    <SegmentCard
                        key={i}
                        segment={s}
                        index={i}
                        columns={columns}
                        count={countFor(s.name.trim())}
                        onChange={(next) => {
                            const copy = [...segments]
                            copy[i] = next
                            setSegments(copy)
                        }}
                        onRemove={() =>
                            setSegments(segments.filter((_, x) => x !== i))
                        }
                    />
                ))}

                <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon sx={{ fontSize: ICON.md }} />}
                        onClick={() =>
                            setSegments([
                                ...segments,
                                newSegment(segments.length),
                            ])
                        }
                    >
                        Add cohort
                    </Button>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                        variant="contained"
                        disabled={!canPreview || loading}
                        onClick={handlePreview}
                    >
                        {loading ? (
                            <CircularProgress
                                size={15}
                                sx={{ color: ON_ORANGE }}
                            />
                        ) : (
                            'Preview split'
                        )}
                    </Button>
                </Box>

                {summary && (
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2.5,
                            background: SURFACE.card,
                            border: `1px solid ${SURFACE.border}`,
                            borderRadius: `${RADIUS.md}px`,
                        }}
                    >
                        <Typography
                            sx={{
                                typography: 'overline',
                                color: ACCENT.text,
                                mb: 1.5,
                            }}
                        >
                            Split result
                        </Typography>
                        <Typography
                            sx={{
                                typography: 'caption',
                                color: TEXT.secondary,
                            }}
                        >
                            {summary.assigned_total.toLocaleString()} of{' '}
                            {summary.total_rows.toLocaleString()} recipients
                            assigned across {summary.segments.length} cohort
                            {summary.segments.length === 1 ? '' : 's'}.
                        </Typography>

                        {summary.unassigned > 0 && (
                            <Typography
                                sx={{
                                    typography: 'caption',
                                    color: STATUS.warning.fg,
                                    mt: 1,
                                }}
                            >
                                {summary.unassigned.toLocaleString()} matched no
                                cohort and would not be sent anything. Add a
                                catch-all as the last cohort to include them.
                            </Typography>
                        )}

                        {summary.overlap_count > 0 && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    mt: 1.5,
                                    alignItems: 'flex-start',
                                }}
                            >
                                <WarningAmberIcon
                                    sx={{
                                        fontSize: ICON.md,
                                        color: STATUS.warning.fg,
                                        mt: '2px',
                                    }}
                                />
                                <Typography
                                    sx={{
                                        typography: 'caption',
                                        color: TEXT.secondary,
                                    }}
                                >
                                    {summary.overlap_count.toLocaleString()}{' '}
                                    recipient
                                    {summary.overlap_count === 1
                                        ? ''
                                        : 's'}{' '}
                                    matched more than one cohort. Each was
                                    claimed by exactly one — the first that
                                    matched — so nobody is sent twice, but the
                                    rules may need tightening.
                                    {summary.shadowed?.length > 0 && (
                                        <>
                                            {' '}
                                            <Box
                                                component="span"
                                                sx={{
                                                    color: STATUS.warning.fg,
                                                }}
                                            >
                                                {summary.shadowed.join(', ')}{' '}
                                                {summary.shadowed.length === 1
                                                    ? 'was'
                                                    : 'were'}{' '}
                                                left with no recipients at all —
                                                every match went to an earlier
                                                cohort, so{' '}
                                                {summary.shadowed.length === 1
                                                    ? 'it is'
                                                    : 'they are'}{' '}
                                                redundant as written.
                                            </Box>
                                        </>
                                    )}
                                </Typography>
                            </Box>
                        )}

                        <Divider sx={{ my: 2 }} />
                        {summary.segments.map((s) => (
                            <Box
                                key={s.name}
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    alignItems: 'center',
                                    mb: 0.75,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Typography
                                    sx={{
                                        typography: 'caption',
                                        fontWeight: 600,
                                        color: TEXT.primary,
                                        minWidth: 120,
                                    }}
                                >
                                    {s.name}
                                </Typography>
                                <Chip
                                    label={s.channel}
                                    size="small"
                                    sx={{
                                        height: 20,
                                        typography: 'caption',
                                        color: ACCENT.text,
                                        background: ACCENT.bg12,
                                    }}
                                />
                                <Typography
                                    sx={{
                                        typography: 'caption',
                                        color: TEXT.muted,
                                        fontFamily: FONT.mono,
                                    }}
                                >
                                    {s.template_id}
                                </Typography>
                                <Box sx={{ flexGrow: 1 }} />
                                <Typography
                                    sx={{
                                        typography: 'caption',
                                        fontWeight: 700,
                                        color: s.count
                                            ? STATUS.success.fg
                                            : TEXT.faint,
                                    }}
                                >
                                    {s.count.toLocaleString()}
                                </Typography>
                            </Box>
                        ))}
                        <Typography
                            sx={{
                                typography: 'caption',
                                color: TEXT.faint,
                                mt: 2,
                            }}
                        >
                            Preview only. Dispatch each cohort through Bulk Send
                            to actually send.
                        </Typography>
                    </Paper>
                )}
            </Box>
        </Box>
    )
}

export default Segments
